export type SuccessPriority = "critical" | "attention" | "stable";

export type SalonSuccessItem = {
  id: number;
  name: string;
  slug?: string;
  priority: SuccessPriority;
  score: number;
  owner: string;
  action: string;
  actionTab: "health" | "billing" | "onboarding" | "integrations";
  signals: string[];
};

const priorityWeight: Record<SuccessPriority, number> = { critical: 3, attention: 2, stable: 1 };

type BillingOverviewSalon = { id: number; isOverdue?: boolean; billingStatus?: string; isDueSoon?: boolean };
type OnboardingOverviewSalon = { id: number; phase?: string; recommendation?: string; progress?: number; onboardingOwner?: string };
type IntegrationOverviewSalon = { id: number; health?: string; alerts?: string[] };

export function buildSalonSuccessCockpit(healthOverview: any, billingOverview: any, onboardingOverview: any, integrationOverview: any): SalonSuccessItem[] {
  const health = Array.isArray(healthOverview?.salons) ? healthOverview.salons : [];
  const billingById = new Map<number, BillingOverviewSalon>((billingOverview?.salons ?? []).map((item: BillingOverviewSalon) => [item.id, item]));
  const onboardingById = new Map<number, OnboardingOverviewSalon>((onboardingOverview?.salons ?? []).map((item: OnboardingOverviewSalon) => [item.id, item]));
  const integrationsById = new Map<number, IntegrationOverviewSalon>((integrationOverview?.salons ?? []).map((item: IntegrationOverviewSalon) => [item.id, item]));

  const items: SalonSuccessItem[] = health.map((salon: any): SalonSuccessItem => {
    const billing = billingById.get(salon.id);
    const onboarding = onboardingById.get(salon.id);
    const integration = integrationsById.get(salon.id);
    const integrationAlerts = integration?.alerts ?? [];
    const signals: string[] = [];
    let severity = salon.level === "critical" ? 4 : salon.level === "attention" ? 2 : 0;
    let action = salon.reasons?.[0] ?? "Acompanhar os indicadores do salão.";
    let actionTab: SalonSuccessItem["actionTab"] = "health";
    if (billing?.isOverdue || billing?.billingStatus === "past_due") { severity += 5; signals.push("Cobrança vencida"); action = "Regularizar cobrança ou registrar o acompanhamento."; actionTab = "billing"; }
    else if (billing?.isDueSoon) { severity += 2; signals.push("Vencimento próximo"); action = "Confirmar a cobrança antes do vencimento."; actionTab = "billing"; }
    if (onboarding?.phase === "stalled") { severity += 3; signals.push("Implantação parada"); if (actionTab === "health") { action = onboarding.recommendation; actionTab = "onboarding"; } }
    else if (onboarding?.phase === "in_progress") signals.push(`${onboarding.progress ?? 0}% da implantação`);
    if (integration?.health === "critical" || integrationAlerts.length > 0) { severity += integration?.health === "critical" ? 3 : 1; signals.push(integrationAlerts[0] ?? "Integração requer atenção"); if (actionTab === "health") { action = integrationAlerts[0] ?? "Revisar integrações configuradas."; actionTab = "integrations"; } }
    if ((salon.reasons ?? []).length > 0) signals.push(salon.reasons[0]);
    const priority: SuccessPriority = severity >= 5 ? "critical" : severity >= 2 ? "attention" : "stable";
    return { id: salon.id, name: salon.name, slug: salon.slug, priority, score: severity, owner: onboarding?.onboardingOwner || "Responsável não definido", action, actionTab, signals: Array.from(new Set(signals)).slice(0, 3) };
  });
  return items.sort((a, b) => priorityWeight[b.priority] - priorityWeight[a.priority] || b.score - a.score || a.name.localeCompare(b.name));
}

export function filterSalonSuccess(items: SalonSuccessItem[], filter: "all" | SuccessPriority) {
  return filter === "all" ? items : items.filter((item) => item.priority === filter);
}
